import { useAppDispatch, useAppSelector } from 'app/store';
import { UpdateSharedLinkEffectData } from 'app/store/Shares/effects.types';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import {
  SetShowUpgradePopupPayload,
  UpgradePopupContextAction,
} from 'app/store/Storage/slice.types';
import { getQuota } from 'app/store/Subscription/selectors';

export const useShowUpgradePopup = ({ audioGuid }: { audioGuid: string }) => {
  const dispatch = useAppDispatch();

  const {
    allowAudioShareLyrics: allowShareLyrics,
    allowAudioShareCredits: allowShareCredits,
    allowAudioShareToBeDownloadable: allowShareToBeDownloadable,
    allowAudioShareToBeRevocable: allowShareToBeRevocable,
  } = useAppSelector(getQuota) || {};

  const getShowUpgradePopupPropsByAction = ({
    action,
  }: {
    action: UpgradePopupContextAction;
  }) =>
    ({
      messageType: 'audio',
      context: {
        audioGuid,
        action,
      },
    } as SetShowUpgradePopupPayload);

  const getShowUpgradePopupProps = ({
    creditsShared,
    lyricsShared,
  }: UpdateSharedLinkEffectData['data']): SetShowUpgradePopupPayload => {
    if (lyricsShared) {
      return getShowUpgradePopupPropsByAction({
        action: 'setAudioShareLinkLyrics',
      });
    }

    if (creditsShared) {
      return getShowUpgradePopupPropsByAction({
        action: 'setAudioShareLinkCredits',
      });
    }

    return getShowUpgradePopupPropsByAction({
      action: 'setAudioShareLinkDownloadable',
    });
  };

  const showUpgradePopupForUpdateShareLink = (
    data: UpdateSharedLinkEffectData['data']
  ) => {
    dispatch(setShowUpgradePopup(getShowUpgradePopupProps(data)));
  };

  const updateShareLinkAllowed = (data: UpdateSharedLinkEffectData['data']) => {
    const showUpgradePopup =
      (data.downloadable && !allowShareToBeDownloadable) ||
      (data.creditsShared && !allowShareCredits) ||
      (data.lyricsShared && !allowShareLyrics);

    if (showUpgradePopup) {
      showUpgradePopupForUpdateShareLink(data);
      return false;
    }
    return true;
  };

  const showUpgradePopupForRevokeShareLink = () => {
    dispatch(
      setShowUpgradePopup(
        getShowUpgradePopupPropsByAction({
          action: 'revokeAudioShareLink',
        })
      )
    );
  };

  const revokeAllowed = () => {
    if (!allowShareToBeRevocable) {
      showUpgradePopupForRevokeShareLink();
      return false;
    }
    return true;
  };

  return {
    updateShareLinkAllowed,
    revokeAllowed,
    showUpgradePopupForUpdateShareLink,
    showUpgradePopupForRevokeShareLink,
  };
};
